import React, { FC, ReactNode, useMemo } from 'react';

import Keys from '../../../Translations/generated/date-range-input.en.json.keys';
import { ApiBaseData } from '@ibe/api';
import { Select, useConfig, useMediaQuery } from '@ibe/components';
import { useTranslation } from 'react-i18next';

export const NUMBER_DAY_PER_WEEK = 7;

const CustomContainer: FC<{
  duration?: number;
  handleDurationChange: (a?: number) => void;
  usePlaceholderAsOption?: boolean;
  children?: React.ReactNode[];
  asSimpleElement?: boolean;
  validationErrorComponent?: (errorKeys?: string[]) => ReactNode;
}> = ({
  duration,
  handleDurationChange,
  usePlaceholderAsOption,
  children,
  asSimpleElement,
  validationErrorComponent
}) => {
  const config = useConfig();
  const { t } = useTranslation('date-range-input');
  const matches = useMediaQuery('(max-width: 600px)');
  const placeholder = t(Keys.durationPlaceholder);
  const placeholderCode = 'placeholder';

  const durationOptions = useMemo(() => {
    const options = config.packageSearchDurationOptions
      ? Object.entries(config.packageSearchDurationOptions).reduce(
          (total: ApiBaseData[], [key, list], idx, array) => {
            return [
              ...total,
              ...list.map(listItem => ({
                code: key === 'WEEKS' ? `${listItem * NUMBER_DAY_PER_WEEK}` : `${listItem}`,
                description: `${listItem} ${
                  key === 'WEEKS'
                    ? t(Keys.week, { count: listItem })
                    : t(Keys.night, { count: listItem })
                }`
              })),
              idx < array.length - 1
                ? { code: '---', description: '---', isDisabled: true }
                : { code: 'filtered', description: 'filtered' }
            ].filter(element => element.code !== 'filtered');
          },
          [] as ApiBaseData[]
        )
      : [];
    if (usePlaceholderAsOption) {
      options.unshift({ code: placeholderCode, description: placeholder });
    }
    return options;
  }, [t]);

  const handleTypeOption = (val: ApiBaseData) => {
    const isPlaceholder = val.code === placeholderCode;
    handleDurationChange(isPlaceholder ? undefined : parseInt(val.code));
  };

  return asSimpleElement ? (
    <Select
      selectedOption={durationOptions.find(option => option.code === duration?.toString())}
      getLabel={(option): string => (option ? option.description : '')}
      getValue={(option): string => (option ? option.code : '')}
      onChange={(val): void => handleTypeOption(val)}
      options={durationOptions}
      placeholder={placeholder}
      id="packageSearchDurationInput"
    />
  ) : (
    <div className="iso__dateRangeInput__customContainer  react-datepicker">
      <div className="iso__dateRangeInput__customContainer__top">
        <div
          className={`iso__dateRangeInput__customContainer__input__container iso__dateRangeInput__customContainer__input__container${
            matches ? '--small' : ''
          }`}
        >
          <div className="iso__dateRangeInput__customContainer__duration__label">
            {t(Keys.chooseDuration)}
          </div>
          <div>
            <Select
              selectedOption={durationOptions.find(option => option.code === duration?.toString())}
              getLabel={(option): string => (option ? option.description : '')}
              getValue={(option): string => (option ? option.code : '')}
              onChange={(val): void => handleTypeOption(val)}
              options={durationOptions}
              placeholder={placeholder}
              id="packageSearchDurationInput"
            />
          </div>
        </div>
        <div className="iso__dateRangeInput__customContainer__calendar__label">
          {t(Keys.chooseRangeLabel)}
        </div>
      </div>
      <div className="iso__dateRangeInput__customContainer__calendar">{children}</div>
      {validationErrorComponent ? (
        <div className="iso__dateRangeInput__customContainer__validation__errors">
          {validationErrorComponent()}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default CustomContainer;
